import { useEffect, useState } from "react";
import API_BASE from "../../../../config";
import { authHeaders, empName } from "../sectionUtils";
import s from "./SectionTeachersTab.module.css";

export default function SectionTeachersTab({ section, semesters = [] }) {
  const [offerings,  setOfferings]  = useState([]);
  const [employees,  setEmployees]  = useState({});
  const [subjects,   setSubjects]   = useState({});
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  const semById = Object.fromEntries(semesters.map(m => [m.id, m]));

  useEffect(() => {
    if (!section?.id) return;
    let cancelled = false;
    setLoading(true); setError("");

    Promise.all([
      fetch(`${API_BASE}/api/class-offerings?sectionId=${section.id}`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_BASE}/api/employees?all=true`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_BASE}/api/subjects`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([offerData, empData, subData]) => {
      if (cancelled) return;
      setOfferings(Array.isArray(offerData) ? offerData : []);
      setEmployees(Object.fromEntries((Array.isArray(empData) ? empData : []).map(e => [e.id, e])));
      setSubjects(Object.fromEntries((Array.isArray(subData) ? subData : []).map(sub => [sub.id, sub])));
      setLoading(false);
    }).catch(() => {
      if (!cancelled) { setError("Could not load teachers."); setLoading(false); }
    });

    return () => { cancelled = true; };
  }, [section?.id]);

  if (loading) return <div className={s.loading}>Loading teachers…</div>;
  if (error)   return <div className={s.errorBanner}>{error}</div>;
  if (offerings.length === 0) return (
    <div className={s.empty}>
      <div className={s.emptyIcon}>👨‍🏫</div>
      <div className={s.emptyText}>No class offerings assigned to this section yet.</div>
    </div>
  );

  /* Group offerings by semester */
  const grouped = new Map();
  offerings.forEach(o => {
    const key = o.semesterId ?? "none";
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(o);
  });

  /* Unique teachers */
  const teacherIds = [...new Set(offerings.map(o => o.teacherStaffId).filter(Boolean))];

  return (
    <div className={s.root}>
      {/* Teacher summary chips */}
      {teacherIds.length > 0 && (
        <div className={s.teacherSummary}>
          <div className={s.summaryLabel}>
            {teacherIds.length} teacher{teacherIds.length !== 1 ? "s" : ""} assigned
          </div>
          <div className={s.teacherChips}>
            {teacherIds.map(id => {
              const emp = employees[id];
              return (
                <div key={id} className={s.teacherChip}>
                  <span className={s.chipAvatar} aria-hidden="true">👨‍🏫</span>
                  <span>{empName(emp) || `Staff #${id}`}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Offerings by semester */}
      {[...grouped.entries()].map(([semId, offers]) => {
        const sem = semId !== "none" ? semById[semId] : null;
        return (
          <div key={semId} className={s.semGroup}>
            <div className={s.semHead}>
              <span className={s.semDot} aria-hidden="true" />
              <span className={s.semLabel}>{sem?.label || "All Semesters"}</span>
            </div>
            <div className={s.offeringList}>
              {offers.map(o => {
                const subject = subjects[o.subjectId];
                const teacher = o.teacherStaffId ? employees[o.teacherStaffId] : null;
                const subjectName = subject?.title || subject?.name || `Subject #${o.subjectId}`;
                return (
                  <div key={o.id} className={s.offeringCard}>
                    <div className={s.subjectName}>{subjectName}</div>
                    <div className={s.offeringMeta}>
                      {teacher && (
                        <span className={s.teacherTag}>
                          <span aria-hidden="true">👨‍🏫</span> {empName(teacher)}
                        </span>
                      )}
                      {!teacher && <span className={s.noTeacher}>No teacher assigned</span>}
                      {o.schedule && (
                        <span className={s.scheduleTag}>
                          <span aria-hidden="true">🕐</span> {o.schedule}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
